import { ToastType } from '@everlywell/leaves';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';

export interface MenuItem {
  title: string;
  submenu: { title: string; link: string }[];
}

export interface Card {
  title: string;
  icon?: {
    url: string;
  };
  link: string;
  description?: {
    childMarkdownRemark?: {
      rawMarkdownBody: string;
    };
  };
}

export interface ButtonProps {
  showAll: boolean;
}

export interface GridProps {
  showAll: boolean;
}

export interface ProductCardProps {
  title: string;
  description?: string;
  icon?: {
    url: string;
  };
  link?: string;
}

export interface ProductCardGridProps {
  backgroundColor?: string;
  title: string;
  cards: Card[];
  sectionHeader: SectionHeaderProps;
  showOverlay?: boolean;
  cardsPerRow?: number;
}

export interface SectionHeaderProps {
  description?: any;
  eyebrow: string;
  title?: string;
  headline?: string;
  text?:
    | string
    | {
        description: {
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
      };
  titleMaxWidth?: string;
}

export interface IContentfulSlider {
  solutions?: Array<{
    navigationLabel: string;
    heading: string;
    description?: { description: string };
    features?: Array<{
      icon: { url: string };
      text: string;
    }>;
    backgroundImage?: { file: { url: string } };
    mobileBackgroundImage?: { file: { url: string } };
    ctaButton?: { url: string; label: string };
  }>;
  slides?: IContentfulSlide[];
  sectionHeader?: {
    eyebrow?: string;
    title?: string;
    headline?: string;
    text?: {
      description: {
        childMarkdownRemark: {
          rawMarkdownBody: string;
        };
      };
    };
  };
}

export interface IContentfulFeature {
  icon: {
    url: string;
  };
  text: string;
}

export interface IContentfulCtaButton {
  url: string;
  label: string;
  isExternal?: boolean;
}

export interface IContentfulSlide {
  heading: string;
  title: string;
  backgroundImage: {
    file: {
      url: string;
    };
  };
  mobileBackgroundImage: {
    file: {
      url: string;
    };
  };
  ctaButton: IContentfulCtaButton;
  description: {
    description: string;
  };
  features: IContentfulFeature[];
  navigationLabel: string;
}

export interface IBenefitsSectionProps {
  features: Array<{
    id: string;
    index: number;
    subHeadline: string;
    featureImage: {
      title: string;
      url: string;
      alt: string;
      gatsbyImageData: null;
      width: number;
      height: number;
    };
    description: string;
    title: string;
    heading: string;
    backgroundColor: string;
    content: string;
    cta: {
      text: string;
      url: string;
    };
    appearance: string;
    container: boolean;
    alignment: string;
    features: never[];
  }>;
  backgroundColor?: string;
  appearance?: string;
  container?: boolean;
  alignment?: string;
  id?: string;
  index?: number;
}

export interface IServiceFeature {
  description: string;
  featureImage: {
    url: string;
  };
}

export interface IStatCard {
  heading: string;
  content: string;
  cta: {
    text: string;
    url: string;
    isIframe: boolean;
  };
}

export interface IStatItem {
  label: string;
  prefix?: string;
  suffix?: string;
  value: string | number;
}

export interface IStatsBarProps {
  stats: IStatItem[];
  backgroundColor?: string;
  textColor?: string;
  labelColor?: string;
  disclaimer?: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
      html: string;
    };
  };
}

export interface ICardProps {
  image: string;
  description: string;
}

export interface IVideoHeroProps {
  cta?: {
    text: string;
    url: string;
  };
  richHeading: {
    raw: string;
  };
  text?: string;
  video: {
    url: string;
  };
  secondaryImage?: {
    file: {
      url: string;
    };
  };
}

interface ITestimonial {
  quote: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  authorName: string;
  authorTitle: string;
  image?: {
    file?: {
      url: string;
    };
  };
}

export interface ITestimonialSliderProps {
  testimonials: ITestimonial[];
}

export interface Slide {
  title: string;
  description: string;
  image: string;
  features: { icon: string; text: string }[];
  cta: { text: string; href: string };
}

export interface IEHSImage {
  url?: string;
  gatsbyImageData: IGatsbyImageData;
  title: string;
}

export interface ICTA {
  text?: string;
  url: string;
  appearance?: string;
  isIframe: boolean;
}

export interface IContentWithImage {
  title?: string;
  heading?: string;
  backgroundColor?: string;
  description: {
    description: string;
    childMarkdownRemark: {
      html: string;
      rawMarkdownBody?: string;
    };
  };
  slug?: string;
  cta?: ICTA;
  image?: IEHSImage;
}

// resources types
export enum RESOURCES_TYPES {
  CASE_STUDY = 'case study',
  WHITE_PAPER = 'white paper',
  BLOG_POST = 'blog post',
  NEWS = 'news',
  PRESS_RELEASE = 'press release',
}

export type TResourceSort = 'most recent' | 'oldest';
export type TResourceType =
  | RESOURCES_TYPES.BLOG_POST
  | RESOURCES_TYPES.WHITE_PAPER
  | RESOURCES_TYPES.CASE_STUDY;

export interface IResource {
  slug: string;
  updatedAt: string;
  type: TResourceType;
  title?: string;
  summary: {
    summary: string;
    childMarkdownRemark: {
      html: string;
    };
  };
  cta: ICTA;
  image: IEHSImage;
}

export interface TCircle {
  radius?: number;
  color: string;
  children?: React.ReactNode;
  className?: string;
}

export interface TRing extends TCircle {
  borderWidth: number;
}

export type TSlide = {
  image: string | React.ReactNode;
  imageAlt: string;
  title?: string;
  copy?: string;
  subtitle?: string;
};

export interface IPartnerContainer {
  content: {
    title?: string;
    headline: string;
    description?: { description: string };
    backgroundColor: string;
    slug?: string;
    cta?: {
      text: string;
      url: string;
    };
  };
}

export interface IPartnerSection {
  testId: string;
  title?: string;
  headline?: string;
  description?: string;
  backgroundColor: string;
  maxWidth?: number;
  slug?: string;
  cta?: {
    text: string;
    url: string;
  };
}

export interface INotification {
  message: string;
  type?: ToastType;
  persist?: boolean;
  details?: JSX.Element | string;
  mixpanelLabel?: string;
}

export type TCarousel = {
  title: string;
  description: string;
  image: IEHSImage;
};

export interface StatCardProps {
  heading: string;
  content: string;
  cta: {
    text: string;
    url: string;
    isIframe: boolean;
  };
}

export type StepType = {
  id?: string | number;
  title: string;
  // ... other properties
};

export interface SolutionSliderProps {
  backgroundColor?: string;
  title: string;
  headline: string;
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  container: any; // You might want to make this more specific
}

export interface HowItWorksProps {
  backgroundColor?: string;
  title: string;
  headline: string;
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  container?: any;
}

/**
 * Interface for the Contentful Blog Post content type
 * This matches the content model in Contentful
 */
export interface ContentfulBlogPost {
  id: string;
  title: string;
  slug: string;
  contentType?: string | string[];
  publishDate: string;
  audience?: string;
  focus?: string;
  featuredImage: {
    gatsbyImageData?: IGatsbyImageData;
    url?: string;
    title: string;
    description: string;
  };
  headerContent?: {
    headerContent?: string;
  };
  pdfAttachment?: {
    url: string;
    title?: string;
    description?: string;
  };
}

export interface BlogPostQueryResult {
  contentfulBlogPost: ContentfulBlogPost;
}

export interface BlogPostsQueryResult {
  allContentfulBlogPost: {
    nodes: ContentfulBlogPost[];
  };
}
